import { Component } from "@theme/component";
import { DialogOpenEvent, emitDialogClose } from "@theme/dialog";
import { getActiveCurrency, sendGaEvent } from "@theme/ga-events";
import { closeDialogElement, showDialogElement } from "@theme/utilities";

/** @type {Record<string, string>} */
const GA_MODAL_OPEN_EVENTS = {
  "compatibility-checker": "compatibility_checker_opened",
};

/**
 * ModalBase - Shared base class for modal dialog components.
 *
 * Provides:
 * - AbortController lifecycle for clean event management
 * - ESC key to close dialog
 * - Backdrop click detection
 * - Wheel event containment within a scrollable container
 * - Focus trapping (Tab key cycling)
 * - Focus management on open
 * - Open/close dialog with body attribute and focus return
 *
 * Subclasses must:
 * - Call `this._bindModalEvents()` in their setup after `super.connectedCallback()`
 * - Have `this.refs.dialog` pointing to the `<dialog>` element
 * - Override `_isBackdropClick(target)` to detect backdrop clicks
 * - Override `_getScrollableElement()` to return the scrollable container
 * - Override `_onModalClose()` which is called when ESC or backdrop click occurs
 * - Optionally override `_getInitialFocusTarget()` for custom initial focus behavior
 *
 * @extends {Component}
 */
class ModalBase extends Component {
  /** @type {HTMLElement | null} */
  firstFocusableElement = null;

  /** @type {HTMLElement | null} */
  lastFocusableElement = null;

  /** @type {AbortController | null} */
  _abortController = null;

  /**
   * True after a successful `_openDialog` until the matching Lenis `dialog:close`.
   * Native dismiss (CloseWatcher / Android back) must clear this via the dialog
   * `close` event — `_closeDialog` alone is not always on that path.
   * @type {boolean}
   */
  #lenisOpenCounted = false;

  /** @type {HTMLElement | null} */
  #returnFocusTo = null;

  /**
   * Remember which control should receive focus when the dialog closes.
   * @param {HTMLElement | null} el
   */
  _setReturnFocus(el) {
    if (el instanceof HTMLElement) this.#returnFocusTo = el;
  }

  connectedCallback() {
    super.connectedCallback();
    this._abortController = new AbortController();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const dialog = this.refs.dialog;
    // Tear-down while open never fires native `close` (and aborting the
    // controller removes that listener) — balance Lenis + clear body lock.
    const needsCleanup = this.#lenisOpenCounted || Boolean(dialog?.open);
    this._abortController?.abort();
    this._abortController = null;
    if (needsCleanup) {
      this.#onNativeDialogClose();
    }
  }

  /** @returns {AbortSignal | undefined} */
  get _signal() {
    return this._abortController?.signal;
  }

  /**
   * Binds shared modal events: ESC key, backdrop click, wheel containment, focus trap.
   * Call this from subclass after setting up refs.
   */
  _bindModalEvents() {
    const signal = this._signal;
    const { dialog } = this.refs;

    // Close on ESC key and handle focus trapping
    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape" && dialog?.open) {
          this._onModalClose();
        }
        this._handleFocusTrap(e);
      },
      { signal },
    );

    // Close on backdrop click
    dialog?.addEventListener(
      "click",
      (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (this._isBackdropClick(target)) {
          this._onModalClose();
        }
      },
      { signal },
    );

    // Native dismiss (CloseWatcher / dialog.close) always fires `close` — pair
    // Lenis here so open/close ref-count stays balanced when `_closeDialog` is skipped.
    dialog?.addEventListener(
      "close",
      () => {
        this.#onNativeDialogClose();
      },
      { signal },
    );

    // Wheel containment on the scrollable element
    const scrollable = this._getScrollableElement();
    scrollable?.addEventListener(
      "wheel",
      (e) => {
        if (!scrollable) return;

        const scrollTarget = this.#resolveWheelScrollTarget(
          e.target,
          scrollable,
          e.deltaY,
        );
        if (scrollTarget) {
          scrollTarget.scrollTop += e.deltaY;
        }

        // Always contain wheel to the modal — never scroll the page behind
        e.preventDefault();
        e.stopPropagation();
      },
      { signal, passive: false },
    );
  }

  /**
   * Shared cleanup after the `<dialog>` closes (imperative or native).
   */
  #onNativeDialogClose() {
    document.body.removeAttribute("data-modal-open");

    if (this.#lenisOpenCounted) {
      this.#lenisOpenCounted = false;
      emitDialogClose(this);
    }

    const target = this.#returnFocusTo || this.refs.openButton;
    this.#returnFocusTo = null;
    if (target instanceof HTMLElement) {
      target.focus();
    }
    this._onDialogClosed();
  }

  /**
   * Hook after the dialog has closed. Override for URL / state cleanup that must
   * also run on native dismiss paths.
   */
  _onDialogClosed() {}

  /**
   * Pick the nearest scroll container (innermost → modal root) that can still
   * move in the wheel direction. Nested regions at an edge fall through to
   * their ancestors instead of trapping the gesture.
   * @param {EventTarget | null} target
   * @param {HTMLElement} rootScrollable
   * @param {number} deltaY
   * @returns {HTMLElement | null}
   */
  #resolveWheelScrollTarget(target, rootScrollable, deltaY) {
    for (const candidate of this.#scrollableAncestors(target, rootScrollable)) {
      if (this.#canScrollVertically(candidate, deltaY)) {
        return candidate;
      }
    }

    return null;
  }

  /**
   * Vertically scrollable ancestors from the event target up through the modal
   * root (innermost first, root last).
   * @param {EventTarget | null} target
   * @param {HTMLElement} rootScrollable
   * @returns {HTMLElement[]}
   */
  #scrollableAncestors(target, rootScrollable) {
    /** @type {HTMLElement[]} */
    const ancestors = [];

    let element = target instanceof Node ? target : null;
    if (element?.nodeType === Node.TEXT_NODE) {
      element = element.parentElement;
    }

    let current = element instanceof Element ? element : null;

    while (current && current !== rootScrollable) {
      if (
        current instanceof HTMLElement &&
        this.#isVerticallyScrollable(current)
      ) {
        ancestors.push(current);
      }
      current = current.parentElement;
    }

    ancestors.push(rootScrollable);
    return ancestors;
  }

  /**
   * @param {HTMLElement} element
   * @param {number} deltaY
   * @returns {boolean}
   */
  #canScrollVertically(element, deltaY) {
    const { scrollTop, scrollHeight, clientHeight } = element;
    if (scrollHeight <= clientHeight + 1) return false;
    if (deltaY > 0) return scrollTop + clientHeight < scrollHeight - 1;
    if (deltaY < 0) return scrollTop > 0;
    return false;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isVerticallyScrollable(element) {
    const { overflowY } = getComputedStyle(element);
    if (
      overflowY !== "auto" &&
      overflowY !== "scroll" &&
      overflowY !== "overlay"
    ) {
      return false;
    }

    return element.scrollHeight > element.clientHeight + 1;
  }

  /**
   * Check if a click target is the modal backdrop. Override in subclasses.
   * @param {HTMLElement} target - The click event target
   * @returns {boolean}
   */
  _isBackdropClick(target) {
    return false;
  }

  /**
   * Get the scrollable element for wheel containment. Override in subclasses.
   * @returns {HTMLElement | null | undefined}
   */
  _getScrollableElement() {
    return null;
  }

  /**
   * Called when ESC or backdrop click triggers close. Override in subclasses.
   */
  _onModalClose() {}

  /**
   * Sets up focus trap: finds focusable elements, stores first/last,
   * and focuses the initial element based on subclass preference.
   */
  _trapFocus() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length === 0) return;

    const firstElement = /** @type {HTMLElement} */ (focusableElements[0]);
    const lastElement = /** @type {HTMLElement} */ (
      focusableElements[focusableElements.length - 1]
    );

    // Update stored elements for the focus trap handler
    this.firstFocusableElement = firstElement;
    this.lastFocusableElement = lastElement;

    // Prefer autofocus element
    const autofocusElement = /** @type {HTMLElement | null} */ (
      dialog.querySelector("[autofocus]")
    );
    if (autofocusElement) {
      autofocusElement.focus();
      return;
    }

    // Let subclass determine default focus target
    const target = this._getInitialFocusTarget(focusableElements);
    if (target) {
      target.focus();
    } else {
      firstElement.focus();
    }
  }

  /**
   * Override to customize which element receives focus when dialog opens.
   * @param {NodeListOf<Element>} _focusableElements - All focusable elements in the dialog
   * @returns {HTMLElement | null}
   */
  _getInitialFocusTarget(_focusableElements) {
    return null;
  }

  /**
   * Handle Tab key for focus trapping within the dialog.
   * @param {KeyboardEvent} e - The keyboard event
   */
  _handleFocusTrap(e) {
    const { dialog } = this.refs;
    if (e.key !== "Tab" || !dialog?.open) return;

    const first = this.firstFocusableElement;
    const last = this.lastFocusableElement;

    if (!first || !last) return;

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Opens the dialog: sets body attribute, shows modal, traps focus.
   * Dispatches `dialog:open` so Lenis (and other dialog listeners) stop page scroll.
   * Matching `dialog:close` is emitted from the native `close` listener so
   * CloseWatcher / Android-back dismiss paths stay paired.
   * @returns {boolean} Whether the dialog opened successfully
   */
  _openDialog() {
    const { dialog } = this.refs;
    if (!dialog) return false;
    if (dialog.open) return true;

    if (!showDialogElement(dialog)) return false;

    if (!this.#returnFocusTo) {
      const active = document.activeElement;
      this.#returnFocusTo =
        active instanceof HTMLElement && active !== document.body
          ? active.closest("button, a[href], [data-modal-trigger]") || active
          : this.refs.openButton;
    }

    document.body.setAttribute("data-modal-open", "true");
    this.#lenisOpenCounted = true;
    this.dispatchEvent(new DialogOpenEvent());
    this._trapFocus();
    return true;
  }

  /**
   * Closes the dialog via `dialog.close()`. Lenis `dialog:close`, body cleanup,
   * and focus return normally run from the native `close` listener so they also
   * cover system dismiss. When `dialog.close` is missing, `closeDialogElement`
   * only drops `open` and never fires `close` — run the same cleanup manually.
   * No-ops when already closed.
   */
  _closeDialog() {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const usedNativeClose = typeof dialog.close === "function";
    closeDialogElement(dialog);
    if (!usedNativeClose) {
      this.#onNativeDialogClose();
    }
  }
}

/**
 * Mysa Modal Component
 *
 * A custom element that manages modal dialogs with URL parameter support,
 * focus trapping, and Typeform compatibility.
 *
 * Features:
 * - Opens/closes modal with trigger button and close button
 * - Restart button reloads embedded content (e.g., Typeform embeds)
 * - ESC key closes modal
 * - Backdrop click closes modal
 * - Focus management and trapping
 * - Page scroll disabled when modal is open via scroll-lock attribute system
 * - Body tag shows modal state (data-modal-open) for Typeform compatibility
 * - URL query string support with browser back/forward navigation
 * - Global event delegation for data-modal-trigger elements
 *
 * Usage:
 * <mysa-modal data-url-parameter="my-modal">
 *   <button ref="openButton">Open</button>
 *   <dialog ref="dialog">
 *     <div class="modal-wrapper" ref="modalWrapper">
 *       <div class="modal">
 *         <div class="modal-inner" ref="modalInner">
 *           <button ref="closeButton">Close</button>
 *           <button ref="restartButton">Restart</button>
 *         </div>
 *       </div>
 *     </div>
 *   </dialog>
 * </mysa-modal>
 *
 * @typedef {Object} MysaModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement} [modalWrapper] - The modal wrapper for backdrop clicks
 * @property {HTMLElement} [modalInner] - The scrollable modal inner container
 * @property {HTMLButtonElement} [openButton] - The trigger button to open modal
 * @property {HTMLButtonElement} [closeButton] - The close button
 * @property {HTMLButtonElement} [restartButton] - Optional restart button for Typeform
 * @property {HTMLButtonElement} [restartButtonMobile] - Mobile footer restart (header hidden on small screens)
 *
 * @extends {ModalBase}
 */
class MysaModal extends ModalBase {
  /** @type {string} */
  urlParameter = "modal";

  /** @type {boolean} */
  #skipUrlOnClose = false;

  connectedCallback() {
    super.connectedCallback();

    // Read URL parameter from dataset
    this.urlParameter =
      this.dataset.urlParameter || this.refs.dialog?.id || "modal";

    this.#syncAccessibleName();
    this.#bindEvents();
    this.#checkUrlOnLoad();
  }

  /**
   * Prefer the visible heading as the dialog name (WCAG 4.1.2). Liquid sets
   * aria-label because nested theme blocks are not readable from the parent.
   */
  #syncAccessibleName() {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;
    const heading = dialog.querySelector(
      ".product-finder__heading, h1, h2, h3",
    );
    if (!(heading instanceof HTMLElement)) return;
    if (!heading.id) heading.id = `${dialog.id}-title`;
    dialog.setAttribute("aria-labelledby", heading.id);
    dialog.removeAttribute("aria-label");
  }

  #bindEvents() {
    const signal = this._signal;
    const { openButton, closeButton, restartButton, restartButtonMobile } =
      this.refs;

    // Open modal
    openButton?.addEventListener(
      "click",
      () => this.openModal(true, openButton),
      { signal },
    );

    // Close modal (ref-bound button)
    closeButton?.addEventListener("click", () => this.closeModal(), { signal });

    // Close modal (any [data-modal-close] element inside this component)
    this.addEventListener(
      "click",
      (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target.closest("[data-modal-close]")) this.closeModal();
      },
      { signal },
    );

    // Restart button - reloads Typeform or other embedded content
    restartButton?.addEventListener("click", () => this.#restartContent(), {
      signal,
    });
    restartButtonMobile?.addEventListener(
      "click",
      () => this.#restartContent(),
      {
        signal,
      },
    );

    // Shared modal events (ESC, backdrop click, wheel containment, focus trap)
    this._bindModalEvents();

    // Handle browser back/forward navigation
    window.addEventListener("popstate", () => this.#checkUrlOnLoad(), {
      signal,
    });
  }

  /**
   * @param {HTMLElement} target
   * @returns {boolean}
   */
  _isBackdropClick(target) {
    const { modalWrapper } = this.refs;
    return (
      target === modalWrapper || target.classList.contains("modal-wrapper")
    );
  }

  _getScrollableElement() {
    return this.refs.modalInner;
  }

  _onModalClose() {
    this.closeModal();
  }

  /**
   * Clear the modal query param after dismiss (including CloseWatcher), unless
   * `closeModal(false)` asked to leave the URL alone.
   */
  _onDialogClosed() {
    if (this.#skipUrlOnClose) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("modal") === this.urlParameter) {
      this.#updateUrl(false);
    }
  }

  /**
   * @param {NodeListOf<Element>} focusableElements
   * @returns {HTMLElement | null}
   */
  _getInitialFocusTarget(focusableElements) {
    // Skip close buttons for initial focus - find first content element
    return (
      /** @type {HTMLElement | undefined} */ (
        Array.from(focusableElements).find(
          (el) => !el.hasAttribute("data-modal-close"),
        )
      ) || null
    );
  }

  #checkUrlOnLoad() {
    const urlParams = new URLSearchParams(window.location.search);
    const modalParam = urlParams.get("modal");
    const { dialog } = this.refs;

    // If URL has our modal parameter, open the modal
    if (modalParam === this.urlParameter) {
      this.openModal(false); // false = don't update URL since we're already here
    } else if (dialog?.open) {
      this.closeModal(false); // false = don't update URL
    }
  }

  /**
   * Opens the modal dialog
   * @param {boolean} [updateUrl=true] - Whether to update the URL with modal parameter
   * @param {HTMLElement | null} [trigger] - Control to restore focus to on close
   */
  openModal(updateUrl = true, trigger = null) {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    if (trigger instanceof HTMLElement) this._setReturnFocus(trigger);

    if (!this._openDialog()) return;

    const gaEventName = GA_MODAL_OPEN_EVENTS[this.urlParameter];
    if (gaEventName) {
      sendGaEvent(gaEventName, {
        action: "modal_opened",
        modal: this.urlParameter,
        product_context: "lv-hvac",
        value: 1,
        currency: getActiveCurrency(),
      });
    }

    // Update URL with query parameter
    if (updateUrl) {
      this.#updateUrl(true);
    }
  }

  /**
   * Closes the modal dialog
   * @param {boolean} [updateUrl=true] - Whether to update the URL to remove modal parameter
   */
  closeModal(updateUrl = true) {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    this.#skipUrlOnClose = !updateUrl;
    try {
      this._closeDialog();
    } finally {
      this.#skipUrlOnClose = false;
    }
  }

  /**
   * Updates the URL with or without the modal parameter
   * @param {boolean} isOpen - Whether the modal is being opened
   */
  #updateUrl(isOpen) {
    const url = new URL(window.location.href);
    const urlParams = url.searchParams;

    if (isOpen) {
      urlParams.set("modal", this.urlParameter);
    } else {
      urlParams.delete("modal");
    }

    const newUrl =
      url.pathname + (urlParams.toString() ? "?" + urlParams.toString() : "");
    window.history.pushState({}, "", newUrl);
  }

  #restartContent() {
    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.dispatchEvent(new CustomEvent("modal:restart", { bubbles: true }));
  }
}

// Global event delegation for data-modal-trigger elements
let globalModalTriggersInitialized = false;

function initGlobalModalTriggers() {
  if (globalModalTriggersInitialized) return;
  globalModalTriggersInitialized = true;

  // Use event delegation to handle clicks on any element with data-modal-trigger
  document.addEventListener("click", (e) => {
    const target = /** @type {HTMLElement | null} */ (e.target);
    const trigger = /** @type {HTMLElement | null} */ (
      target?.closest("[data-modal-trigger]")
    );
    if (!trigger) return;

    const modalParameter = trigger.dataset.modalTrigger;
    if (!modalParameter) return;

    // Find the modal with matching URL parameter
    const modal = /** @type {MysaModal | null} */ (
      document.querySelector(
        `mysa-modal[data-url-parameter="${modalParameter}"]`,
      )
    );

    if (modal?.openModal) {
      modal.openModal(true, trigger);
    }
  });
}

// Initialize when DOM is ready
function initMysaModal() {
  if (!customElements.get("mysa-modal")) {
    customElements.define("mysa-modal", MysaModal);
  }
  initGlobalModalTriggers();
}

// Auto-initialize if DOM is already loaded, otherwise wait for DOMContentLoaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMysaModal);
} else {
  initMysaModal();
}

// Export for manual initialization if needed
export { ModalBase, MysaModal, initMysaModal, initGlobalModalTriggers };
